import { useCallback, useMemo, useState } from 'react';
import { CalEventContent } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/Components/CalEventContent';
interface BusyEventContentProps {
  eventInfo: any;
  onSelect?: ({
    startStr,
    onTopOfEvent,
  }: {
    startStr: string;
    onTopOfEvent: boolean;
  }) => void;
  snapToInterval?: boolean; // Optional flag to enable/disable snapping (default: true)
  intervalMinutes?: number; // Optional interval duration in minutes (default: 15)
  interviewDataRef?: React.MutableRefObject<{
    minimum_notice_config?: {
      value: number;
      type: 'hours' | 'days';
    };
    minimum_notice_enabled?: boolean;
  }>;
}

export const BusyEventContent: React.FC<BusyEventContentProps> = ({
  eventInfo,
  onSelect,
  snapToInterval = true, // Default to snapping to intervals
  intervalMinutes = 15, // Default to 15-minute intervals
  interviewDataRef,
}) => {
  const { event, timeText } = eventInfo || {};
  const { extendedProps } = event || {};
  const { light: bg, dark: color, subject, userInfo } = extendedProps || {};

  // State to track current mouse position cursor style
  const [cursorStyle, setCursorStyle] = useState<'default' | 'pointer'>(
    'default'
  );

  const minimumNoticeEnabled =
    interviewDataRef?.current?.minimum_notice_enabled || false;
  const minimumNoticeTimeHours = (() => {
    const minimumNotice = interviewDataRef?.current?.minimum_notice_config;
    if (!minimumNotice) return 0;

    if (minimumNotice.value && minimumNotice.type) {
      return minimumNotice.type === 'days'
        ? minimumNotice.value * 24
        : minimumNotice.value;
    }

    return 0;
  })();

  // Calculate if the event has any future portion
  const hasFuturePortion = useMemo(() => {
    const now = new Date();
    const eventEnd = event?.end;
    return eventEnd && eventEnd > now;
  }, [event?.end]);

  const meetsMinimumNotice = useMemo(() => {
    if (!minimumNoticeEnabled || minimumNoticeTimeHours <= 0) {
      return true;
    }

    const now = new Date();
    const eventStart = event?.start;
    if (!eventStart) return false;

    const minimumNoticeTimeMs = minimumNoticeTimeHours * 60 * 60 * 1000;
    const timeUntilEvent = eventStart.getTime() - now.getTime();

    return timeUntilEvent >= minimumNoticeTimeMs;
  }, [event?.start, minimumNoticeEnabled, minimumNoticeTimeHours]);

  const isEventClickable = useMemo(() => {
    return hasFuturePortion && meetsMinimumNotice;
  }, [hasFuturePortion, meetsMinimumNotice]);

  // Handle mouse move to dynamically update cursor style
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isEventClickable) {
        setCursorStyle('default');
        return;
      }

      // Get the container dimensions and mouse position
      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      const totalHeight = rect.height;

      // Calculate the proportion of where the mouse is
      const mouseProportion = mouseY / totalHeight;

      // Calculate time at mouse position
      const startTime = event.start.getTime();
      const endTime = event.end.getTime();
      const durationMs = endTime - startTime;
      const mouseTimeMs = startTime + durationMs * mouseProportion;
      const mouseDate = new Date(mouseTimeMs);

      // Check if the mouse is over a future or past portion
      const now = new Date();
      const minimumNoticeTimeMs = minimumNoticeTimeHours * 60 * 60 * 1000;
      const timeUntilMousePosition = mouseDate.getTime() - now.getTime();

      const isMouseOverClickableArea =
        mouseDate > now &&
        (!minimumNoticeEnabled ||
          timeUntilMousePosition >= minimumNoticeTimeMs);

      setCursorStyle(isMouseOverClickableArea ? 'pointer' : 'default');
    },
    [
      event?.start,
      event?.end,
      isEventClickable,
      minimumNoticeEnabled,
      minimumNoticeTimeHours,
    ]
  );

  // Handle click event and snap to nearest interval if enabled
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onSelect || !isEventClickable) return;

      // Get the container dimensions and click position
      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const totalHeight = rect.height;

      // Calculate the proportion of where the click occurred
      const clickProportion = clickY / totalHeight;

      // Calculate time difference between start and end
      const startTime = event.start.getTime();
      const endTime = event.end.getTime();
      const durationMs = endTime - startTime;

      // Calculate exact clicked time
      const clickedTimeMs = startTime + durationMs * clickProportion;
      let finalDate = new Date(clickedTimeMs);

      // Check if the clicked time is in the past
      const now = new Date();
      if (finalDate < now) {
        // If clicked time is in the past, don't proceed
        return;
      }

      if (minimumNoticeEnabled && minimumNoticeTimeHours > 0) {
        const minimumNoticeTimeMs = minimumNoticeTimeHours * 60 * 60 * 1000;
        const timeUntilClickedTime = finalDate.getTime() - now.getTime();

        if (timeUntilClickedTime < minimumNoticeTimeMs) {
          return;
        }
      }

      // Snap to nearest interval if snapToInterval is true
      if (snapToInterval) {
        const minutesSinceEpoch =
          finalDate.getHours() * 60 + finalDate.getMinutes();
        const nextInterval =
          Math.ceil(minutesSinceEpoch / intervalMinutes) * intervalMinutes;
        finalDate.setHours(
          Math.floor(nextInterval / 60),
          nextInterval % 60,
          0,
          0
        );
        finalDate.setSeconds(0, 0);

        // Ensure the snapped time stays within event bounds
        if (finalDate >= event.end) {
          finalDate = new Date(event.end);
          finalDate.setMinutes(finalDate.getMinutes() - intervalMinutes, 0, 0); // Last valid interval
        }

        // Ensure the snapped time still meets minimum notice requirements
        if (minimumNoticeEnabled && minimumNoticeTimeHours > 0) {
          const minimumNoticeTimeMs = minimumNoticeTimeHours * 60 * 60 * 1000;
          const timeUntilSnappedTime = finalDate.getTime() - now.getTime();

          if (timeUntilSnappedTime < minimumNoticeTimeMs) {
            return;
          }
        }
      }

      // Convert to ISO string
      const isoTime = finalDate.toISOString();

      // Call the onSelect callback with the ISO timestamp
      onSelect({ startStr: isoTime, onTopOfEvent: true });
    },
    [
      event?.start,
      event?.end,
      onSelect,
      isEventClickable,
      snapToInterval,
      intervalMinutes,
      minimumNoticeEnabled,
      minimumNoticeTimeHours,
    ]
  );

  // If the event is entirely in the past, use default cursor
  const staticCursorStyle = useMemo(() => {
    if (!isEventClickable) return 'default';
    return undefined; // Let the dynamic cursor handling take over
  }, [isEventClickable]);

  return (
    <div
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setCursorStyle('default')}
      style={{
        height: '100%',
        cursor: staticCursorStyle || cursorStyle,
      }}
    >
      <CalEventContent
        eventInfo={eventInfo}
        title={subject}
        time={timeText}
        interviewer={userInfo?.name}
        colors={{ bg, color }}
      />
    </div>
  );
};

// note: use the below for getting custom time, where ever the user clicked

// const handleClick = useCallback(
// 	(e: React.MouseEvent<HTMLDivElement>) => {
// 		if (!onSelect || !event?.start || isPastEvent) return;

// 		// Get the container dimensions and click position
// 		const container = e.currentTarget;
// 		const rect = container.getBoundingClientRect();
// 		const clickY = e.clientY - rect.top;
// 		const totalHeight = rect.height;

// 		// Calculate the proportion of where the click occurred
// 		const clickProportion = clickY / totalHeight;

// 		// Calculate time difference between start and end
// 		const startTime = event.start.getTime();
// 		const endTime = event.end.getTime();
// 		const durationMs = endTime - startTime;

// 		// Calculate exact clicked time
// 		const clickedTimeMs = startTime + durationMs * clickProportion;
// 		const clickedDate = new Date(clickedTimeMs);

// 		// Convert to ISO string
// 		const isoTime = clickedDate.toISOString();

// 		// Call the onSelect callback with the ISO timestamp
// 		if (!onSelect || !event?.start) return;

// 		onSelect?.({ startStr: isoTime });
// 	},
// 	[event?.start, event?.end, onSelect, isPastEvent]
// );
